// src/pages/quality/NcrNew.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormContentManager from "../../../components/common/form/FormContentManager";
import { getQualityMeta } from "../../../api/quality/meta";
import TableCell from "../../../components/common/ui/TableCell";
import PeopleCellLoader from "../../../components/common/people/PeopleCellLoader";

export default function NcrNew() {
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const navigate = useNavigate();
  const [dns, setDns] = useState(doc_dns || "");
  const [requestPeople, setRequestPeople] = useState([]);

  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";


  // 상단 공통 폼 (문서 공통 메타)
  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    title: "",
    note: "",
    result: "", // 최종 판정(조직에 따라 사용, 유지)
    proposer: "", // 제안자/보고자
  });

  // NCR 메타 (부적합 사건 정보 위주)
  const [ncr, setNcr] = useState({
    location: "", // 발생장소 *
    vendor: "", // 업체
    action_dept: "", // 조치부서(책임부서 개념) *
    has_attachment: "no", // 유첨 여부
    nc_type: "", // 부적합형태 *
    description: "", // 부적합 내용 *
    immediate_action: "", // 즉시/임시 조치 (있다면)
  });

  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onNcr = (k, v) => setNcr((p) => ({ ...p, [k]: v }));

  // 자동 제목
  const autoTitle = useMemo(() => {
    const t = ncr.nc_type?.trim();
    const l = ncr.location?.trim();
    if (t && l) return `[NCR] ${t} - ${l}`;
    if (l) return `[NCR] ${l}`;
    if (t) return `[NCR] ${t}`;
    return "";
  }, [ncr.nc_type, ncr.location]);

  // 간단 검증
  const errors = useMemo(() => {
    const e = {};
    if (!form.doc_date) e.doc_date = "작성일을 선택하세요.";
    if (!ncr.location?.trim()) e.location = "발생장소를 입력하세요.";
    if (!ncr.action_dept?.trim()) e.action_dept = "조치부서를 입력하세요.";
    if (!ncr.nc_type?.trim()) e.nc_type = "부적합형태를 선택하세요.";
    if (!ncr.description?.trim()) e.description = "부적합 내용을 입력하세요.";
    if (!form.proposer?.trim()) e.proposer = "제안자를 입력하세요.";
    return e;
  }, [form.doc_date, form.proposer, ncr]);

  useEffect(() => {
    if (!dns) return;

    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns });
        const meta = res?.meta || {};
        if (meta.form) {
          setForm((prev) => ({ ...prev, ...meta.form }));
        }
        if (meta.ncrMeta) {
          setNcr((prev) => ({ ...prev, ...meta.ncrMeta }));
        }
      } catch (err) {
        console.error(err);
        alert("NCR 데이터를 불러오지 못했습니다.");
      }
    }
    loadDetail();
  }, [dns]);

  // 저장 payload
  const getBody = useCallback(
  () => ({
    layout: null,
    values: {
      project: projectId,
      module: "quality",
      doc_type: "NCR",
      dns,

      // ⭐ 모든 NCR 데이터를 meta 로 묶기
      meta: {
        form: {
          ...form,
          title: form.title?.trim() || autoTitle,
        },
        ncrMeta: ncr,
        __validation__: errors,
        __client__: { form_type: "NC" },
      },
    },
  }),
  [projectId, dns, form, ncr, autoTitle, errors]
);

  // 오른쪽 요약
  const summaryConfig = {
    title: "NCR 요약",
    sections: [
      { label: "제목", value: form.title?.trim() || autoTitle || "-" },
      { label: "상태", value: form.status || "-" },
      { label: "작성일", value: form.doc_date || "-" },
      { label: "발생장소", value: ncr.location || "-" },
      { label: "업체", value: ncr.vendor || "-" },
      { label: "조치부서", value: ncr.action_dept || "-" },
      { label: "유첨", value: ncr.has_attachment === "yes" ? "유" : "무" },
      { label: "부적합형태", value: ncr.nc_type || "-" },
      {
        label: "최종 판정",
        value:
          form.result === "pass"
            ? "적합"
            : form.result === "fail"
              ? "부적합"
              : "-",
      },
      { label: "제안자", value: form.proposer || "-" },
    ],
    blocks: [
      { label: "부적합 내용", content: ncr.description || "입력 없음" },
      {
        label: "즉시/임시조치",
        content: ncr.immediate_action || "입력 없음",
      },
    ],
    footerNote: "부적합 발생 사실을 정확히 기록하세요.",
  };

  const BOARD = `/project/${projectId}/quality/qualityboard`;




  return (
    <FormContentManager
      params={{
        module: "quality",
        doc_type: "NC",
        mode: 3,
        scope: "project",
        project: projectId,
        dns,
      }}
      getBody={getBody}
      onChangeId={(newDns) => {
        setDns(newDns);
      }}
      redirectTo={BOARD}
      docName="부적합보고서 (NCR)"
      docDescribe="부적합 발생 사실과 기본 정보를 기록합니다."
      summaryConfig={summaryConfig}
      requestPeople={requestPeople}
    >
      {/* 기본 정보 */}
      <table className="w-full border border-gray-300 mt-4">
        <tbody>
          <tr>
            <TableCell
              label="제목"
              editing
              colSpan={3}
              value={form.title}
              input={
                <input
                  className="mt-1 w-full rounded border px-2 py-1.5"
                  placeholder={autoTitle || "문서 제목"}
                  value={form.title}
                  onChange={(e) => onForm("title", e.target.value)}
                />
              }
            />
          </tr>
          <tr>
            <TableCell
              label="발생일"
              editing
              value={form.doc_date}
              input={
                <>
                  <input
                    type="date"
                    className="mt-1 w-full rounded border px-2 py-1.5"
                    value={form.doc_date}
                    onChange={(e) => onForm("doc_date", e.target.value)}
                  />
                  {errors.doc_date && (
                    <p className="mt-1 text-xs text-red-600">{errors.doc_date}</p>
                  )}
                </>
              }
            />

            <TableCell
              label="상태"
              editing
              value={form.status}
              input={
                <select
                  className="mt-1 w-full rounded border px-2 py-1.5 bg-white"
                  value={form.status}
                  onChange={(e) => onForm("status", e.target.value)}
                >
                  <option value="draft">작성중</option>
                  <option value="in_progress">조치중</option>
                  <option value="pending_verify">검증대기</option>
                  <option value="closed">종결</option>
                </select>
              }
            />
          </tr>
          <tr>
            <TableCell
              label="최종 판정"
              editing
              value={form.result}
              input={
                <select
                  className="mt-1 w-full rounded border px-2 py-1.5 bg-white"
                  value={form.result}
                  onChange={(e) => onForm("result", e.target.value)}
                >
                  <option value="">선택</option>
                  <option value="pass">적합</option>
                  <option value="fail">부적합</option>
                </select>
              }
            />
            <TableCell
              label="제안자"
              editing
              value={form.proposer}
              input={
                <>
                  <input
                    className="mt-1 w-full rounded border px-2 py-1.5"
                    value={form.proposer}
                    onChange={(e) => onForm("proposer", e.target.value)}
                    placeholder="이름 / 부서"
                  />
                  {errors.proposer && (
                    <p className="mt-1 text-xs text-red-600">{errors.proposer}</p>
                  )}
                </>
              }
            />
          </tr>

          <tr>
            <TableCell
              label="발생장소 *"
              editing
              colSpan={1}
              value={ncr.location}
              input={
                <>
                  <input
                    className="mt-1 w-full rounded border px-2 py-1.5"
                    value={ncr.location}
                    onChange={(e) => onNcr("location", e.target.value)}
                    placeholder="예: B2 슬래브"
                  />
                  {errors.location && (
                    <p className="mt-1 text-xs text-red-600">{errors.location}</p>
                  )}
                </>
              }
            />

            <TableCell
              label="업체"
              editing
              value={ncr.vendor}
              input={
                <input
                  className="mt-1 w-full rounded border px-2 py-1.5"
                  value={ncr.vendor}
                  onChange={(e) => onNcr("vendor", e.target.value)}
                  placeholder="시공/납품 업체"
                />
              }
            />
          </tr>

          
          <tr>
            <TableCell
              label="유첨"
              editing
              value={ncr.has_attachment}
              input={
                <div className="mt-1 flex gap-4">
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      checked={ncr.has_attachment === "yes"}
                      onChange={() => onNcr("has_attachment", "yes")}
                    />
                    유
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      checked={ncr.has_attachment === "no"}
                      onChange={() => onNcr("has_attachment", "no")}
                    />
                    무
                  </label>
                </div>
              }
            />
            <TableCell
              label="부적합형태 *"
              editing
              colSpan={3}
              value={ncr.nc_type}
              input={
                <>
                  <select
                    className="mt-1 w-full md:w-1/2 rounded border px-2 py-1.5 bg-white"
                    value={ncr.nc_type}
                    onChange={(e) => onNcr("nc_type", e.target.value)}
                  >
                    <option value="">선택</option>
                    <option value="규격미달">규격미달</option>
                    <option value="외관불량">외관불량</option>
                    <option value="치수허용초과">치수허용초과</option>
                    <option value="문서미비">문서미비</option>
                    <option value="공정미준수">공정미준수</option>
                    <option value="기타">기타</option>
                  </select>
                  {errors.nc_type && (
                    <p className="mt-1 text-xs text-red-600">{errors.nc_type}</p>
                  )}
                </>
              }
            />
          </tr>
<tr>
            {/* <TableCell
              label="조치부서 *"
              editing
              value={ncr.action_dept}
              input={
                <>
                  <input
                    className="mt-1 w-full rounded border px-2 py-1.5"
                    value={ncr.action_dept}
                    onChange={(e) => onNcr("action_dept", e.target.value)}
                    placeholder="예: 건품팀"
                  />
                  {errors.action_dept && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.action_dept}
                    </p>
                  )}
                </>
              }
            /> */}
            <TableCell
              label="조치 담당자"
              colSpan={3}
              editing
              input={
                <PeopleCellLoader
                  dns={dns}
                  value={requestPeople}
                  onChange={(list) => setRequestPeople(list)}
                />
              }
            />
          </tr>
          <tr>
            <TableCell
              label="부적합 내용 *"
              editing
              colSpan={3}
              rich={false}
              value={ncr.description}
              input={
                <>
                  <textarea
                    className="mt-1 w-full rounded border px-3 py-2 h-32 leading-relaxed"
                    value={ncr.description}
                    onChange={(e) => onNcr("description", e.target.value)}
                    placeholder="발생 경위, 기준 대비 차이, 사진 설명 등"
                  />
                  {errors.description && (
                    <p className="mt-1 text-xs text-red-600">{errors.description}</p>
                  )}
                </>
              }
            />
          </tr>

          <tr>
            <TableCell
              label="즉시/임시조치"
              editing
              colSpan={3}
              value={ncr.immediate_action}
              input={
                <textarea
                  className="mt-1 w-full rounded border px-3 py-2 h-24"
                  value={ncr.immediate_action}
                  onChange={(e) => onNcr("immediate_action", e.target.value)}
                  placeholder="예: 불합격품 격리, 임시조치 사항 등"
                />
              }
            />
          </tr>
        </tbody>
      </table>

    </FormContentManager>
  );
}
